import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { Route } from './+types/home';
import { apiFetch } from '~/lib/api-client';
import type { ApiResult, EventDto } from '~/lib/types';
import {
  currentYm,
  etToday,
  etToUnix,
  dateStrToUnixEndOfDayEt,
  rangeMonth,
  rangeNextWeek,
  rangeThisWeek,
  shiftYm,
} from '~/lib/event-utils';
import { EventAgenda, EmptyState, EventRow } from '~/components/event-list';
import { FilterBox } from '~/components/filter-box';
import { AiSearch } from '~/components/ai-search';
import { MonthGrid, buildDayInfo } from '~/components/month-grid';
import { SubscribeMenu } from '~/components/subscribe-menu';
import { SubmitLink } from '~/components/submit-modal';
import { useCopy, Accented } from '~/lib/copy';

/**
 * The page's own name, from the city it is serving.
 *
 * This was hard-coded to Tampa. On any second city the browser tab, the search
 * result and every social preview would have announced the wrong city's name —
 * the most visible possible version of the single-tenant assumption.
 *
 * `noindex` while the city has nothing published: an empty calendar indexed on
 * day one is what Google remembers the site for, and it takes weeks to undo.
 */
export function meta({ data }: Route.MetaArgs) {
  const siteName = data?.siteName ?? 'Networking Calendar';
  const displayName = data?.displayName ?? '';
  const description =
    data?.tagline ||
    `The live calendar of business networking events across ${displayName} — chamber mixers, BNI, meetups and workshops, one place, always current.`;

  return [
    { title: `${siteName} — every networking event in ${displayName}` },
    { name: 'description', content: description },
    { property: 'og:title', content: siteName },
    { property: 'og:description', content: description },
    { property: 'og:image', content: '/og-default.png' },
    { property: 'og:type', content: 'website' },
    ...(data?.dayOne ? [{ name: 'robots', content: 'noindex, follow' }] : []),
  ];
}

export async function loader({ request }: Route.LoaderArgs) {
  const url = new URL(request.url);
  const p = url.searchParams;
  const view = p.get('view') ?? 'week';
  const ym = p.get('month') ?? currentYm();
  const area = p.get('area') ?? '';
  const category = p.get('category') ?? '';
  const cost = p.get('cost') ?? '';
  const time = p.get('time') ?? '';
  const sort = p.get('sort') ?? '';
  const q = p.get('q') ?? '';
  const aiq = p.get('aiq') ?? '';
  const note = p.get('note') ?? '';
  const widened = p.get('widened') === '1';
  const when = p.get('when') ?? '';

  // Search mode: an AI query, a keyword, or an explicit `when` overrides the
  // week/next/month range and searches the resolved window (AI) or all-upcoming.
  const searchMode = Boolean(aiq || q || when);

  // /api/events expects from/to as UNIX SECONDS. range*() return ET dayKeys —
  // convert (start-of-day for `from`, end-of-day for `to`) or the Number() parse
  // NaNs out and the date filter is silently dropped (all events show).
  const startUnix = (dayKey: string) => {
    const [y = 1970, mo = 1, d = 1] = dayKey.split('-').map(Number);
    return etToUnix(y, mo, d, 0, 0);
  };
  const setRange = (r: { from: string; to: string }) => {
    qs.set('from', String(startUnix(r.from)));
    qs.set('to', String(dateStrToUnixEndOfDayEt(r.to)));
  };

  const qs = new URLSearchParams();
  if (searchMode) {
    qs.set('when', when || 'all');
  } else if (view === 'month') {
    setRange(rangeMonth(ym));
  } else if (view === 'next') {
    setRange(rangeNextWeek());
  } else {
    setRange(rangeThisWeek());
  }

  if (area) qs.set('area', area);
  if (category) qs.set('category', category);
  if (cost) qs.set('cost', cost);
  if (time) qs.set('time', time);
  if (sort) qs.set('sort', sort);
  if (q) qs.set('q', q);

  const res = await apiFetch(request, `/api/events?${qs.toString()}`);
  let events: EventDto[] = [];
  if (res.ok) {
    const body = (await res.json()) as ApiResult<{ events: EventDto[] }>;
    if (body.ok) events = body.data.events;
  }

  // The area filter's options are THIS city's rows, not a list baked into the
  // bundle. Adding a neighbourhood is a row, not a deploy.
  let areas: { slug: string; label: string }[] = [];
  let siteName = '';
  let displayName = '';
  let tagline = '';
  let dayOne = false;
  const cfg = await apiFetch(request, '/api/config');
  if (cfg.ok) {
    const body = (await cfg.json()) as ApiResult<{
      areas: { slug: string; label: string }[];
      siteName: string;
      displayName: string;
      tagline: string;
      dayOne: boolean;
    }>;
    if (body.ok) {
      areas = body.data.areas ?? [];
      siteName = body.data.siteName ?? '';
      displayName = body.data.displayName ?? '';
      tagline = body.data.tagline ?? '';
      dayOne = body.data.dayOne === true;
    }
  }

  return {
    events,
    areas,
    view,
    ym,
    area,
    category,
    cost,
    time,
    sort,
    q,
    aiq,
    note,
    widened,
    when,
    searchMode,
    siteName,
    displayName,
    tagline,
    dayOne,
    host: url.host,
    todayKey: etToday().dayKey,
  };
}

export default function Home({ loaderData }: Route.ComponentProps) {
  const {
    events,
    areas,
    view,
    ym,
    area,
    category,
    cost,
    time,
    sort,
    q,
    aiq,
    note,
    widened,
    when,
    searchMode,
    host,
    todayKey,
    dayOne,
  } = loaderData;
  const t = useCopy();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [search, setSearch] = useState(q);

  // Keep the keyword field in sync when the applied query changes (AI search).
  useEffect(() => setSearch(q), [q]);

  /** Apply a param patch. Any manual change clears the AI result banner. */
  function patchParams(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v);
      else next.delete(k);
    }
    for (const k of ['aiq', 'note', 'widened']) next.delete(k);
    setSearchParams(next, { preventScrollReset: true });
    setSelectedDay(null);
  }

  /** Switch the browse view — exits search mode entirely. */
  function selectView(v: 'week' | 'next' | 'month') {
    patchParams({ view: v === 'week' ? '' : v, month: '', when: '', q: '' });
    setSearch('');
  }

  function submitKeyword() {
    const text = search.trim();
    patchParams({ q: text, when: text ? 'all' : '' });
  }

  function clearSearch() {
    patchParams({ view: '', month: '', when: '', q: '' });
    setSearch('');
  }

  const activeView = searchMode ? '' : view;
  const hasFilters = Boolean(area || category || cost || time || q);
  const dayInfo = buildDayInfo(events);
  const dayEvents = selectedDay ? events.filter((e) => e.dayKey === selectedDay) : [];
  const windowLabel = searchMode
    ? 'in results'
    : view === 'month'
      ? 'this month'
      : view === 'next'
        ? 'next week'
        : 'this week';

  // Subscribe feed scope — carry the active filters (not the browse window) so
  // subscribers get only their slice. A specific search window is honored too.
  const subscribeScope = {
    area,
    category,
    cost,
    time,
    when: searchMode && when && when !== 'all' ? when : undefined,
  };

  return (
    <main>
      {/* ── Hero — image-led, AI search front and center ── */}
      <section className="hero">
        <div className="hero-media" aria-hidden="true" />
        <div className="container">
          <div className="hero-inner">
            <p className="hero-eyebrow">{t('home.hero.eyebrow')}</p>
            <h1 className="hero-title">
              <Accented text={t('home.hero.title')} />
            </h1>
            <p className="hero-sub">{t('home.hero.sub')}</p>
            <div className="hero-search">
              <AiSearch variant="hero" />
            </div>
            <div className="hero-actions">
              <SubmitLink className="ui-btn btn-secondary on-dark btn-lg">
                {t('home.hero.cta')}
              </SubmitLink>
            </div>
            <p className="hero-meta">
              <span className="live-dot" aria-hidden="true" /> Updated today ·{' '}
              <span className="t-num">
                {events.length} event{events.length === 1 ? '' : 's'}
              </span>{' '}
              {windowLabel}
            </p>
          </div>
        </div>
        <div className="wave" aria-hidden="true">
          <svg viewBox="0 0 1440 80" preserveAspectRatio="none" focusable="false">
            <path
              d="M0,44 C240,84 480,80 720,50 C960,20 1200,18 1440,50 L1440,80 L0,80 Z"
              fill="var(--paper)"
            />
          </svg>
        </div>
      </section>

      {/* ── Calendar ── */}
      <section className="band band--paper band-tight">
        <div className="container">
          <div className="section-head">
            <div>
              <span className="eyebrow">Find events</span>
              <h2 className="section-title">Browse the calendar</h2>
            </div>
            <SubscribeMenu host={host} scope={subscribeScope} />
          </div>

          <div className="stack calendar-controls">
            <div className="row-between calendar-viewrow">
              <div className="segmented" role="group" aria-label="Calendar view">
                <button
                  type="button"
                  aria-pressed={activeView === 'week' ? 'true' : 'false'}
                  onClick={() => selectView('week')}
                >
                  This week
                </button>
                <button
                  type="button"
                  aria-pressed={activeView === 'next' ? 'true' : 'false'}
                  onClick={() => selectView('next')}
                >
                  Next week
                </button>
                <button
                  type="button"
                  className="seg-month"
                  aria-pressed={activeView === 'month' ? 'true' : 'false'}
                  onClick={() => selectView('month')}
                >
                  <svg width="15" height="15" viewBox="0 0 16 16" aria-hidden="true">
                    <rect
                      x="1.5"
                      y="2.5"
                      width="13"
                      height="12"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path d="M1.5 6h13" stroke="currentColor" strokeWidth="1.5" />
                    <path
                      d="M5 1v3M11 1v3"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  Month
                </button>
              </div>
              <span className="muted small results-count t-num" aria-live="polite">
                {events.length} event{events.length === 1 ? '' : 's'} {windowLabel}
              </span>
            </div>

            <FilterBox
              areas={areas}
              value={{ area, category, cost, time, sort, q }}
              onChange={patchParams}
              collapsible
              keyword={{ value: search, onChange: setSearch, onSubmit: submitKeyword }}
            />
          </div>

          {searchMode && (
            <div className="ai-result-banner" role="status">
              {aiq && <span className="tag-ai">AI</span>}
              <span>
                {aiq
                  ? note || `Results for “${aiq}”.`
                  : q
                    ? `Showing matches for “${q}”.`
                    : 'Showing all upcoming matches.'}
                {widened && <span className="muted"> Widened to keyword matches.</span>}
              </span>
              <button type="button" className="btn-link small" onClick={clearSearch}>
                Clear search
              </button>
            </div>
          )}

          <div className="calendar-body">
            {searchMode ? (
              events.length === 0 ? (
                <MonthEmpty hasFilters={hasFilters} dayOne={dayOne} />
              ) : (
                <EventAgenda events={events} todayKey={todayKey} />
              )
            ) : view === 'month' ? (
              <div className="stack">
                <MonthGrid
                  ym={ym}
                  dayInfo={dayInfo}
                  todayKey={todayKey}
                  selectedDay={selectedDay}
                  onSelectDay={setSelectedDay}
                  onShiftMonth={(delta) =>
                    patchParams({ view: 'month', month: shiftYm(ym, delta) })
                  }
                />
                {selectedDay && dayEvents.length > 0 ? (
                  <div>
                    <h3 className="selected-day-head">Events on {dayEvents[0]?.dateLabel}</h3>
                    <div className="day-events day-events--bare">
                      {dayEvents.map((e) => (
                        <EventRow key={e.id} event={e} />
                      ))}
                    </div>
                  </div>
                ) : selectedDay ? null : events.length === 0 ? (
                  <MonthEmpty hasFilters={hasFilters} dayOne={dayOne} />
                ) : (
                  <p className="muted small month-hint">
                    Pick a day with dots to see its events here.
                  </p>
                )}
              </div>
            ) : events.length === 0 ? (
              <MonthEmpty hasFilters={hasFilters} dayOne={dayOne} />
            ) : (
              <EventAgenda events={events} todayKey={todayKey} />
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

/**
 * Three different silences, and they must not look the same.
 *
 * - **Day one** — the city has published nothing at all. A first visitor
 *   reading "nothing matches these filters" concludes the site is broken, so
 *   this one says plainly that the calendar is opening and invites the first
 *   listing. It is the difference between "new" and "abandoned".
 * - **Filtered** — they asked for something specific and it isn't there.
 * - **This stretch** — the week or month happens to be quiet.
 *
 * The copy names the city it is on. It used to say "where the Bay looks first"
 * on every city, which is wrong everywhere except one.
 */
function MonthEmpty({ hasFilters, dayOne }: { hasFilters: boolean; dayOne: boolean }) {
  const t = useCopy();

  if (dayOne && !hasFilters) {
    return (
      <EmptyState title={t('home.empty.dayOne.title')}>
        <>
          <p>{t('home.empty.dayOne.body')}</p>
          <SubmitLink className="ui-btn btn-primary">{t('home.empty.dayOne.cta')}</SubmitLink>
        </>
      </EmptyState>
    );
  }

  return (
    <EmptyState title={hasFilters ? t('home.empty.filtered.title') : t('home.empty.window.title')}>
      <>
        <p>{t('home.empty.body')}</p>
        <SubmitLink className="ui-btn btn-primary">{t('home.empty.cta')}</SubmitLink>
      </>
    </EmptyState>
  );
}
